import {PlusCircleOutlined} from '@ant-design/icons';
import {Button, Checkbox, Popconfirm} from 'antd';
import cn from 'classnames';
import {omitBy} from 'lodash-es';
import React, {createRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {v4 as uid} from 'uuid';

import {CustomRadio} from 'components/@common/widgets/custom-radio';
import {IconsMap} from 'components/dynamic-icon';
import {
    TPredicateExpressionConnectionParams,
    IPredicateExpressionGroupRef,
} from 'features/predicate-expression-form-list';
import {isEmptyObject} from 'shared/utils';

import {TRuleProp} from '../../api/types';
import {IRulesOperandRef} from '../../model/types';
import {RulesOperand} from './rules-operand';
import styles from './rules.module.less';

interface IRuleGroupProps {
    initialExpression: TRuleProp;
    className?: cn.Argument;
    isChild?: boolean;
    remove?: () => void;
    depth: number;
    itemIndex: string;
    // Для проверки isDirty и isEmpty
    onRulesChange?: (operands?: TRuleProp[]) => void;
}

export const RulesGroup = forwardRef<IPredicateExpressionGroupRef, IRuleGroupProps>(
    ({initialExpression, className, isChild, remove, depth, itemIndex, onRulesChange}: IRuleGroupProps, ref) => {
        const fillOperandsWithUid = (operands: TRuleProp[]) =>
            operands.map((oper) => {
                if (!Array.isArray(oper)) {
                    return {uid: oper.uid ?? uid(), ...oper};
                }

                return oper;
            });

        const [identificator, setIdentificator] = useState<TPredicateExpressionConnectionParams>({
            uid: uid(),
            enabled: true,
            ...initialExpression[0],
        });

        const [operands, setOperands] = useState<TRuleProp[]>(fillOperandsWithUid(initialExpression.slice(1)));

        const operandRefs = useRef<{current: IRulesOperandRef}[]>([]);
        operandRefs.current = operands.map((_, i) => operandRefs.current[i] ?? createRef());

        const getValues = async (skipValidation?: boolean) => {
            try {
                const list = operandRefs.current ?? [];

                const valuesFromOperand = await Promise.all(list.map((item) => item.current.getValues(skipValidation)));

                const values = valuesFromOperand.filter((vals) => {
                    if (typeof vals === 'object' && isEmptyObject(omitBy(vals, (v) => !v))) return false;
                    if (Array.isArray(vals) && vals.length === 1) return false;
                    return true;
                });

                return [{connectBy: identificator.connectBy, enabled: identificator.enabled}, ...values];
            } catch (e) {
                throw new Error(e);
            }
        };

        const setExpression = (exp: TRuleProp) => {
            setIdentificator({uid: exp[0]?.uid ?? uid(), ...exp[0]});
            setOperands(fillOperandsWithUid(exp.slice(1) as TRuleProp[]));
        };

        useImperativeHandle(ref, () => ({getValues, setExpression}));

        return (
            <div className={cn(styles['rules-group-group'], {[styles['rules-group-group_main']]: !isChild}, className)}>
                <div className="fw-500 mb-1-5 d-flex align-items-center gap-2">
                    {!depth && itemIndex === '1' ? 'Фильтр' : `Группа №${itemIndex}`}
                    <div>
                        <Checkbox
                            checked={identificator.enabled}
                            onChange={(e) => {
                                onRulesChange?.(operands);
                                setIdentificator((p) => ({...p, enabled: e.target.checked}));
                            }}
                        >
                            Активно
                        </Checkbox>
                    </div>
                </div>
                <div
                    className={cn(styles['rules-group-group__upbar'], {
                        [styles['rules-group-group__upbar_sticky']]: !depth,
                    })}
                >
                    <div className="d-flex gap-1-5 align-items-center">
                        <CustomRadio
                            value={identificator.connectBy}
                            onChange={(e) => {
                                onRulesChange?.(operands);
                                setIdentificator((p) => ({...p, connectBy: e.target.value}));
                            }}
                            style={{background: 'white'}}
                            items={[
                                {label: 'Оператор «ИЛИ»', value: 'OR'},
                                {label: 'Оператор «И»', value: 'AND'},
                            ]}
                        />
                    </div>
                </div>
                <div className={styles['rules-group-group__operands']}>
                    <div className={cn({[styles['rules-group-group__operands__border']]: operands.length})} />
                    <div className={styles['rules-group-group__operands__body']}>
                        {operands.map((oper, index) => (
                            <RulesOperand
                                ref={operandRefs.current[index]}
                                operand={oper}
                                key={(() => {
                                    const key = !Array.isArray(oper) ? oper.uid : (oper?.[0].uid ?? uid());
                                    return key;
                                })()}
                                remove={() => {
                                    setOperands(() => {
                                        const newOpers = operands.filter((_v, i) => i !== index);
                                        onRulesChange?.(newOpers);
                                        return newOpers;
                                    });
                                }}
                                depth={depth + 1}
                                itemIndex={!depth && itemIndex === '1' ? `${index + 1}` : `${itemIndex}.${index + 1}`}
                                onRulesChange={onRulesChange}
                            />
                        ))}
                        {!operands.length && (
                            <div className={styles['rules-group-group__operands__body__no-rules']}>
                                Необходимо добавить правило или группу
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className={cn('d-flex gap-1 align-items-center justify-content-end', {
                        'mt-2': !!operands.length,
                        'mt-5': !operands.length,
                    })}
                >
                    <Button
                        type="default"
                        onClick={async () => {
                            const lastOperand = operandRefs.current[operandRefs?.current?.length - 1];
                            const lastOperandValues = await lastOperand?.current?.getValues?.(true);

                            const prefilledData = (() => {
                                if (!lastOperandValues || Array.isArray(lastOperandValues)) return {};
                                const {operatorCode, fieldName, tableName} = lastOperandValues;
                                return {operatorCode, fieldName, tableName};
                            })();

                            onRulesChange?.(operands);
                            setOperands((p) => [
                                ...p,
                                {
                                    tableName: prefilledData?.tableName,
                                    fieldName: prefilledData?.fieldName,
                                    operatorCode: prefilledData?.operatorCode,
                                    enabled: true,
                                    comparisonValue: '',
                                    uid: uid(),
                                },
                            ]);
                        }}
                    >
                        <PlusCircleOutlined />
                        Добавить правило
                    </Button>
                    <Button
                        type="default"
                        onClick={() => {
                            onRulesChange?.(operands);
                            setOperands((p) => [...p, [{connectBy: 'OR', uid: uid(), enabled: true}]]);
                        }}
                    >
                        <PlusCircleOutlined />
                        Добавить группу
                    </Button>
                    <Popconfirm
                        title={<span>Вы действительно хотите удалить группу?</span>}
                        placement="bottomLeft"
                        okText="Удалить"
                        disabled={!operands.length || !isChild}
                        onConfirm={() => {
                            remove?.();
                        }}
                    >
                        <div>
                            <Button
                                disabled={!isChild}
                                type="default"
                                className={cn('btn-only-icon', 'button-critic')}
                                onClick={() => {
                                    if (!operands.length) remove?.();
                                }}
                            >
                                <IconsMap.TrashXOutlined />
                            </Button>
                        </div>
                    </Popconfirm>
                </div>
            </div>
        );
    }
);
